import { useMemo } from 'react';

import { PLAN_ENABLEMENT_FLAG_NAMES_LIST } from '../config/planEnablement.config';

import {
  type GetFlagFn,
  getMergedPlanEnablement,
  isFeatureInMarketplaceEnablement,
  isMergedPlanProductFeatureEnabled,
} from './enablement.helpers';

export type PlanFeatureAccessInputs = {
  /** Resolver for flag values (e.g. from useFeatureFlags().getFlag); {@link GetFlagFn} */
  getFlag: GetFlagFn;
  /** Entitlement check for feature (e.g. from useEntitlements().hasFeature) */
  hasFeature: (
    featureCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => boolean;
  /** Entitlement check for product (e.g. from useEntitlements().hasProduct) */
  hasProduct: (
    productCode: string,
    assetId?: string,
    assetType?: 'vehicle' | 'peripheral'
  ) => boolean;
  /**
   * LD keys merged for plan enablement (default: {@link PLAN_ENABLEMENT_FLAG_NAMES_LIST} — same
   * telematics + protect merge as {@link useEntitlements} / {@link hasFeatureAccess}).
   */
  planEnablementFlagNames?: string[];
  /** LaunchDarkly key for marketplace enablement flag (optional) */
  marketplaceEnablementFlagName?: string;
  /** Product code (e.g. 'OST') */
  productCode: string;
  /** Feature code (e.g. 'MAPS') */
  featureCode: string;
};

export type PlanFeatureAccessResult = {
  /** User has the feature/product from entitlements data */
  isEntitled: boolean;
  /** Merged plan map allows this product/feature pair (non-empty merged map, non-empty product row, feature listed) */
  isPlanFeatureEnabled: boolean;
  /** Marketplace enablement allows this product/feature pair (false if no marketplace flag); legacy LD array values gate by product only */
  isPlanMarketplaceEnabled: boolean;
  /** Show feature nav item and content (with mixed-plan messaging when !isEntitled && isPlanMarketplaceEnabled) */
  showFeature: boolean;
  /** User can manage the plan/feature subscription in BPS/Marketplace */
  canManageSubscription: boolean;
};

/**
 * Composable hook: combines injected `getFlag`, `hasFeature`, and `hasProduct` with merged plan
 * enablement and optional marketplace catalog — without calling `useFeatureFlags` or reading Redux.
 *
 * **When to use**
 * - Prefer {@link usePlanMarketplaceAccess} for vehicle/org surfaces that need combined plan + marketplace
 *   resolution, `hasEffectiveFeatureAccess`, mixed-plan copy, and `isUserContextIdentified` from the
 *   feature-flags provider context.
 * - Use this hook when you want explicit injection (tests, non-default wiring) or to keep composition
 *   in the caller. Plan pair semantics match **`isMergedPlanProductFeatureEnabled`** (strict empty **P**;
 *   inject `hasFeature` / `hasProduct` from **`useEntitlements`** when you want the same entitlement + merge stack).
 *
 * **Contracts:** Pass **referentially stable** `getFlag` / `hasFeature` / `hasProduct` when possible
 * (e.g. from hooks). Entitlement callbacks are only invoked with `(featureCode)` / `(productCode)` —
 * for asset-scoped UX, wrap `hasFeature` / `hasProduct` so they close over `assetId` / `assetType`.
 *
 * When `marketplaceEnablementFlagName` is omitted, `isPlanMarketplaceEnabled` and `canManageSubscription`
 * stay `false` (marketplace branch disabled).
 *
 * @param options - getFlag, hasFeature, hasProduct, optional flag name overrides, productCode, featureCode
 * @returns isEntitled, isPlanFeatureEnabled, isPlanMarketplaceEnabled, showFeature, canManageSubscription
 */
export function usePlanFeatureAccess(
  options: PlanFeatureAccessInputs
): PlanFeatureAccessResult {
  const {
    getFlag,
    hasFeature,
    hasProduct,
    planEnablementFlagNames = PLAN_ENABLEMENT_FLAG_NAMES_LIST,
    marketplaceEnablementFlagName,
    productCode,
    featureCode,
  } = options;

  const mergedPlanEnablement = useMemo(
    () => getMergedPlanEnablement(getFlag, planEnablementFlagNames),
    [getFlag, planEnablementFlagNames]
  );

  return useMemo((): PlanFeatureAccessResult => {
    const isEntitled = hasFeature(featureCode) || hasProduct(productCode);
    const isPlanFeatureEnabled = isMergedPlanProductFeatureEnabled(
      mergedPlanEnablement,
      productCode,
      featureCode
    );
    const marketplaceFlagValue = marketplaceEnablementFlagName
      ? getFlag(marketplaceEnablementFlagName)
      : undefined;
    const isPlanMarketplaceEnabled = marketplaceEnablementFlagName
      ? isFeatureInMarketplaceEnablement(
          marketplaceFlagValue,
          productCode,
          featureCode
        )
      : false;

    const showFeature =
      isPlanFeatureEnabled && (isEntitled || isPlanMarketplaceEnabled);
    const canManageSubscription =
      isEntitled && isPlanFeatureEnabled && isPlanMarketplaceEnabled;

    return {
      isEntitled,
      isPlanFeatureEnabled,
      isPlanMarketplaceEnabled,
      showFeature,
      canManageSubscription,
    };
  }, [
    featureCode,
    getFlag,
    hasFeature,
    hasProduct,
    marketplaceEnablementFlagName,
    mergedPlanEnablement,
    productCode,
  ]);
}
